import * as Progress from '@radix-ui/react-progress';
import { useEffect, useRef, useState } from 'react';
import tinycolor from 'tinycolor2';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { ProgressBarProps, ProgressIndicatorProps } from './ProgressBar.types';

const PROGRESS_BAR_ANIMATION_CONFIG: KeyframeAnimationOptions = {
  duration: 660,
  easing: 'ease-in-out',
  fill: 'forwards',
};

const baseLabelClasses =
  'tw:left-1 tw:text-xs tw:text-sq-text-color tw:dark:text-sq-dark-text tw:items-center tw:h-4.5';

const ProgressIndicator = (
  props: ProgressIndicatorProps & { max: number; valuesLength: number; index: number; totalValue: number },
) => {
  const {
    value,
    color = undefined,
    testId,
    label,
    extraClasses = '',
    labelClasses = '',
    valuesLength,
    max,
    index,
    totalValue,
    ...tooltipProps
  } = props;
  const tooltipData = getQTipData(tooltipProps);

  const previousWidth = useRef(0);
  const animationRef = useRef<Animation | null>(null);
  const indicatorElementRef = useRef<HTMLDivElement | null>(null);
  const [textColorClass, setTextColorClass] = useState('');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Determine which corners to round based on position and total value
  let roundedCorners = '';

  if (totalValue >= max) {
    // If progress is 100%, round both ends
    if (index === 0 && valuesLength === 1) {
      roundedCorners = 'tw:rounded-[0.9375rem]';
    } else if (index === 0) {
      roundedCorners = 'tw:rounded-l-[0.9375rem]';
    } else if (index === valuesLength - 1) {
      roundedCorners = 'tw:rounded-r-[0.9375rem]';
    }
  } else {
    // Otherwise, only round the leftmost indicator
    if (index === 0) {
      roundedCorners = 'tw:rounded-l-[0.9375rem]';
    }
  }

  const bgColor = color || undefined;
  const bgClass = color ? '' : 'tw:bg-sq-theme-dark';
  const indicatorTestId = testId ? `progress-bar-indicator-${testId}` : 'progress-bar-indicator';
  const newWidth = (Number(value) / Number(max)) * 100;

  useEffect(() => {
    if (!indicatorElementRef.current) {
      return;
    }

    function cleanupAnimation() {
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }
    }

    const oldWidth = previousWidth.current;

    cleanupAnimation();

    indicatorElementRef.current.style.width = `${newWidth}%`;

    if (!prefersReducedMotion && newWidth !== oldWidth) {
      animationRef.current = indicatorElementRef.current.animate(
        [{ width: `${oldWidth}%` }, { width: `${newWidth}%` }],
        PROGRESS_BAR_ANIMATION_CONFIG,
      );
    }

    previousWidth.current = newWidth;

    return () => cleanupAnimation();
  }, [newWidth, prefersReducedMotion]);

  useEffect(() => {
    const computeTextClass = (elem: HTMLElement) => {
      const computedStyle = getComputedStyle(elem);
      const backgroundColor = computedStyle.backgroundColor;
      const textColorClass = tinycolor(backgroundColor).isDark()
        ? 'tw:text-sq-white tw:dark:text-sq-white'
        : 'tw:text-sq-text-color tw:dark:text-sq-text-color';
      setTextColorClass(textColorClass);
    };
    if (indicatorElementRef.current) {
      computeTextClass(indicatorElementRef.current);
    }
  }, [color]);

  return (
    <Progress.Indicator
      className={`tw:w-full tw:h-4.5 tw:flex tw:justify-center tw:items-center ${
        valuesLength === 1 ? '' : 'tw:overflow-hidden'
      } ${roundedCorners} ${bgClass} ${extraClasses}`}
      {...tooltipData}
      data-qtip-text={tooltipProps.tooltip ? tooltipProps.tooltip : `${value}%`}
      data-segment-value={value}
      data-testid={indicatorTestId}
      ref={indicatorElementRef}
      style={{
        // Background color will default to the theme color if undefined
        backgroundColor: bgColor,
        width: prefersReducedMotion ? `${newWidth}%` : undefined,
      }}>
      {label ? (
        <span
          className={`${baseLabelClasses} ${labelClasses} ${textColorClass} ${
            valuesLength === 1 ? 'tw:absolute tw:z-50' : ''
          }`}>
          {label}
        </span>
      ) : undefined}
    </Progress.Indicator>
  );
};

const ProgressBar = ({
  values = [],
  max = 100,
  containerExtraClasses = '',
  zeroValueLabel = 'No progress yet',
}: ProgressBarProps) => {
  const totalValue = values.reduce((acc, { value }) => acc + value, 0);
  return (
    <Progress.Root
      className={`tw:relative tw:h-4.5 tw:w-full tw:overflow-hidden tw:rounded-[0.9375rem] tw:bg-sq-dark-gray tw:dark:bg-sq-dark-disabled-gray tw:flex tw:flex-1 ${containerExtraClasses}`}
      max={max}
      value={totalValue}>
      {totalValue > 0 ? (
        values.map((props, i) => {
          return (
            <ProgressIndicator
              {...props}
              max={max}
              valuesLength={values.length}
              index={i}
              totalValue={totalValue}
              key={i}
            />
          );
        })
      ) : (
        <div className={`${baseLabelClasses} tw:mx-auto tw:overflow-hidden`}>{zeroValueLabel}</div>
      )}
    </Progress.Root>
  );
};

export default ProgressBar;
